<template>
  <section class="pb-15">
    <v-form @submit.prevent="onSubmit" validate-on="input">
      <v-progress-linear :active="loading" :indeterminate="loading" absolute />
      <v-card elevation="4" class="mt-4">
        <v-tabs v-model="activeTab" align-tabs="start">
          <v-tab value="site" color="primary">Site Information</v-tab>
          <v-tab value="opening-hours" color="primary">Opening Hours</v-tab>
          <v-tab value="contacts" color="primary">Contacts</v-tab>
          <v-tab value="site-image" color="primary">Site Image</v-tab>
        </v-tabs>

        <v-tabs-window v-model="activeTab">
          <v-tabs-window-item value="site">
            <div class="pa-4">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    :loading="loading"
                    :autofocus="true"
                    :rules="[() => !!site.name || 'Name is required']"
                    v-model="site.name"
                    label="Name"
                  />
                </v-col>
                <v-spacer />
              </v-row>
              <v-row>
                <v-col class="d-flex justify-start">
                  <v-radio-group
                    v-model="site.isExcludedFromAI"
                    color="primary"
                    inline
                  >
                    <template v-slot:label>
                      <span
                        >Is this site <strong>Excluded</strong> from AI
                        Analysis?</span
                      >
                    </template>
                    <v-radio label="Yes" :value="true"></v-radio>
                    <v-radio label="No" :value="false"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="d-flex justify-start">
                  <v-radio-group
                    v-model="site.hasDigitalTwin"
                    color="primary"
                    inline
                  >
                    <template v-slot:label>
                      <span>Does this site have a Digital Twin?</span>
                    </template>
                    <v-radio label="Yes" :value="true"></v-radio>
                    <v-radio label="No" :value="false"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <div class="v-input__slot">
                    <label-component
                      label="Address Lookup"
                      :labelBold="false"
                      :valueIndent="false"
                    >
                      <input
                        label="Lookup"
                        v-model="site.address"
                        ref="autocomplete"
                        type="text"
                        placeholder="type all or part of a place"
                        class="v-text-field search-location"
                      />
                    </label-component>
                  </div>
                </v-col>
              </v-row>

              <v-row justify="space-between">
                <v-col cols="12" sm="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.streetNumber"
                    label="Number"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.address"
                    label="Address Lookup"
                    hint="Type all or part of a place"
                    ref="autocomplete"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.country"
                    :rules="[() => !!site.country || 'Country is required']"
                    label="Country"
                  />
                </v-col>
              </v-row>

              <v-row justify="space-between">
                <v-col cols="4">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.city"
                    :rules="[() => !!site.city || 'City is required']"
                    label="City or District"
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.state"
                    :rules="[() => !!site.state || 'State is required']"
                    label="Region, State, or Territory"
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.postalCode"
                    :rules="[
                      () => !!site.postalCode || 'Postal Code is required',
                    ]"
                    label="Postal Code"
                  />
                </v-col>
              </v-row>

              <v-row justify="space-between">
                <v-col cols="3">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="siteCoordinatesLat"
                    :rules="[
                      () => !!site.latitude || 'Latitude is required',
                      () => {
                        const latStr = site.latitude.toString();
                        const decimalPart = latStr.includes('.')
                          ? latStr.split('.')[1]
                          : '';
                        return (
                          decimalPart.length === 5 ||
                          'Latitude must have exactly 5 decimal digits'
                        );
                      },
                    ]"
                    label="Latitude"
                  />
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="siteCoordinatesLng"
                    :rules="[
                      () => !!site.longitude || 'Longitude is required',
                      () => {
                        const lngStr = site.longitude.toString();
                        const decimalPart = lngStr.includes('.')
                          ? lngStr.split('.')[1]
                          : '';
                        return (
                          decimalPart.length === 5 ||
                          'Longitude must have exactly 5 decimal digits'
                        );
                      },
                    ]"
                    label="Longitude"
                  />
                </v-col>

                <v-col cols="3">
                  <v-select
                    variant="outlined"
                    density="compact"
                    v-model="site.timeZoneId"
                    :items="timezones"
                    :custom-filter="timezoneFilter"
                    :rules="[
                      () => !!site.timeZoneId || 'Time Zone is required',
                    ]"
                    item-title="name"
                    item-value="id"
                    label="Time Zone"
                  />
                </v-col>
                <v-col cols="3">
                  <v-autocomplete
                    :items="weatherStations"
                    item-title="name"
                    item-value="id"
                    v-model="site.weatherStationId"
                    label="Weather Station"
                    required
                    variant="outlined"
                    density="compact"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="3">
                  <v-select
                    variant="outlined"
                    density="compact"
                    v-model="site.utilityCompanyIds"
                    :rules="[
                      () => !!site.utilityCompanyIds || 'Utility is required',
                    ]"
                    :items="utilityCompanies"
                    :item-title="(item) => item.name || ''"
                    :item-value="(item) => item.id"
                    label="Utility"
                    multiple
                  />
                </v-col>

                <v-col cols="3">
                  <v-select
                    variant="outlined"
                    density="compact"
                    :items="marketContexts"
                    item-title="name"
                    item-value="id"
                    v-model="site.marketContextId"
                    label="Market Context"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.kwhCost"
                    :rules="[
                      () => !!site.kwhCost || 'Cost per kWh is required',
                    ]"
                    label="Cost per kWh ($)"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.thermCost"
                    label="Cost per Therm ($)"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.waterCost"
                    label="Cost per Gal ($)"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.cO2Threshold"
                    label="CO2 Threshold"
                    type="number"
                    min="800"
                    max="1500"
                    step="1"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.thresholdkW"
                    label="Threshold kW"
                    type="number"
                    step="1"
                  />
                </v-col>
              </v-row>

              <v-row justify="space-between">
                <v-col cols="4">
                  <v-select
                    variant="outlined"
                    density="compact"
                    :rules="[
                      () => !!site.siteTypeId || 'Building Type is required',
                    ]"
                    :items="siteTypes"
                    item-title="name"
                    item-value="id"
                    v-model="site.siteTypeId"
                    label="Building Type"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.squareFootage"
                    :rules="[
                      () => !!site.squareFootage || 'Sq. Ft. is required',
                    ]"
                    label="Sq. Ft."
                    type="number"
                    min="0"
                    max="50000"
                    step="500"
                  />
                </v-col>

                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.floors"
                    :rules="[() => !!site.floors || 'Floors is required']"
                    label="Floors"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    variant="outlined"
                    density="compact"
                    v-model="site.occupants"
                    :rules="[() => !!site.occupants || 'Occupants is required']"
                    label="Occupants"
                    type="number"
                    min="0"
                    max="10000"
                    step="50"
                  />
                </v-col>
                <v-col cols="2">
                  <v-select
                    variant="outlined"
                    density="compact"
                    :rules="[() => !!site.year || 'Year Built is required']"
                    :items="years"
                    v-model="site.year"
                    label="Year Built"
                  />
                </v-col>
              </v-row>
            </div>
          </v-tabs-window-item>
          <v-tabs-window-item value="opening-hours">
            <div class="pa-4">
              <v-row>
                <v-col cols="8">
                  <div class="text-h6 text-primary">
                    Opening Hours (optional)
                  </div>
                </v-col>
                <v-col cols="4" class="d-flex justify-end align-center">
                  <v-btn
                    :color="allOpen ? 'primary' : 'secondary'"
                    @click="toggleAllOpeningHours(!allOpen)"
                  >
                    <v-icon start>{{
                      allOpen ? 'mdi-toggle-switch-off' : 'mdi-toggle-switch'
                    }}</v-icon>
                    {{ allOpen ? 'Switch Days Off' : 'Switch Days On' }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="2">
                  <span class="text-primary font-weight-bold">Weekday</span>
                </v-col>
                <v-col cols="1">
                  <span class="text-primary font-weight-bold">Is Open</span>
                </v-col>
                <v-col cols="1">
                  <span class="text-primary font-weight-bold">24 Hours</span>
                </v-col>
                <v-col cols="3">
                  <span class="text-primary font-weight-bold"
                    >Opening Time</span
                  >
                </v-col>
                <v-col cols="3">
                  <span class="text-primary font-weight-bold"
                    >Closing Time</span
                  >
                </v-col>
              </v-row>
              <v-row v-for="day in siteStartStopTimesNew" :key="day.dayOfWeek">
                <v-col cols="2">
                  <span class="text-primary font-weight-bold">
                    {{ day.dayOfWeekName }}
                  </span>
                </v-col>
                <v-col cols="1" class="pa-0 d-flex align-center">
                  <v-switch
                    v-model="day.isOpen"
                    color="success"
                    hide-details
                    density="compact"
                    class="ml-4 pa-0"
                    style="min-height: 32px"
                  />
                </v-col>
                <v-col cols="1" class="pa-0 d-flex align-center">
                  <v-switch
                    v-model="day.is24h"
                    hide-details
                    @update:model-value="handleIs24hChange(day)"
                    color="success"
                    :disabled="!day.isOpen"
                    density="compact"
                    class="ml-4 pa-0"
                    style="min-height: 32px"
                  />
                  <!-- <div v-else style="min-width: 48px; min-height: 32px"></div> -->
                </v-col>
                <v-col cols="3">
                  <v-select
                    variant="outlined"
                    density="compact"
                    v-model="day.startStopTime.siteStartTime"
                    :items="generateTimeOptions24h(false)"
                    item-title="text"
                    item-value="value"
                    :disabled="!day.isOpen || day.is24h"
                  />
                </v-col>
                <v-col cols="3">
                  <v-select
                    variant="outlined"
                    density="compact"
                    v-model="day.startStopTime.siteEndTime"
                    :items="generateTimeOptions24h(true)"
                    item-title="text"
                    item-value="value"
                    :disabled="!day.isOpen || day.is24h"
                  />
                </v-col>
              </v-row>
            </div>
          </v-tabs-window-item>

          <v-tabs-window-item value="contacts">
            <div class="pa-4">
              <v-row>
                <v-col cols="8">
                  <div class="text-h6 text-primary">Contacts (optional)</div>
                </v-col>
                <v-col cols="2">
                  <v-btn color="primary" @click="addContact">Add Contact</v-btn>
                </v-col>
              </v-row>
              <v-row
                justify="space-between"
                v-for="(contact, index) in site.updateSiteContactResourceModels"
                :key="contact.contactMethodId"
              >
                <v-col>
                  <v-card class="pa-4" elevation="2">
                    <v-row>
                      <v-col cols="4">
                        <v-text-field
                          variant="outlined"
                          density="compact"
                          v-model="contact.siteContactName"
                          :rules="[
                            () =>
                              !!contact.siteContactName ||
                              'Contact Name is required',
                          ]"
                          label="Contact Name"
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          variant="outlined"
                          density="compact"
                          v-model="contact.siteContactTitle"
                          :rules="[
                            () =>
                              !!contact.siteContactTitle ||
                              'Contact Title is required',
                          ]"
                          label="Contact Title"
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          variant="outlined"
                          density="compact"
                          :items="siteContactNotificationFrequencies"
                          :rules="[
                            (v) => !!v || 'Contact Frequency is required',
                          ]"
                          v-model="contact.siteContactNotificationFrequencyId"
                          label="Contact Frequency"
                          item-title="name"
                          item-value="id"
                        />
                      </v-col>
                    </v-row>
                    <v-row
                      v-for="(
                        contactMethod, methodIndex
                      ) in contact.updateSiteContactMethodResourceModels"
                      :key="contactMethod.contactMethodId"
                    >
                      <v-col cols="4">
                        <v-select
                          variant="outlined"
                          density="compact"
                          :items="contactTypes"
                          v-model="contactMethod.contactMethodId"
                          :rules="[(v) => !!v || 'Contact Type is required']"
                          label="Contact Type"
                          item-title="text"
                          item-value="value"
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          variant="outlined"
                          density="compact"
                          v-if="contactMethod.contactMethodId !== 2"
                          v-model="contactMethod.siteContactMethodValue"
                          :rules="[
                            () =>
                              !!contactMethod.siteContactMethodValue ||
                              'Number is required',
                          ]"
                          label="Number"
                          masked
                        />
                        <v-text-field
                          variant="outlined"
                          density="compact"
                          v-if="contactMethod.contactMethodId === 2"
                          v-model="contactMethod.siteContactMethodValue"
                          :rules="[
                            () =>
                              !!contactMethod.siteContactMethodValue ||
                              'Email is required',
                            (email) =>
                              /.+@.+\..+/.test(email) || 'E-mail must be valid',
                          ]"
                          label="Email"
                        />
                      </v-col>
                      <v-col cols="2">
                        <v-switch
                          v-model="contactMethod.isPreferred"
                          :label="`Preferred`"
                          @update:modelValue="
                            togglePrefer(methodIndex, contactMethod, contact)
                          "
                          color="success"
                          hide-details
                          density="compact"
                        />
                      </v-col>
                      <v-col cols="2" v-if="methodIndex === 0">
                        <v-btn
                          color="primary"
                          @click="addContactMethod(contact)"
                          v-bind:disabled="
                            contact.updateSiteContactMethodResourceModels
                              .length >= 2
                          "
                        >
                          Add Method
                        </v-btn>
                      </v-col>
                      <v-col cols="2" v-if="methodIndex > 0">
                        <v-btn
                          color="primary"
                          @click="removeContactMethod(contact, methodIndex)"
                        >
                          Remove Method
                        </v-btn>
                      </v-col>
                      <v-col cols="2">
                        <v-btn color="primary" @click="removeContact(index)">
                          Remove Contact
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-divider class="mt-6" />
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-tabs-window-item>

          <v-tabs-window-item value="site-image">
            <div class="pa-4">
              <v-row>
                <v-col cols="8">
                  <div class="text-h6 text-primary">Site Image (optional)</div>
                </v-col>
                <v-col cols="8">
                  <v-file-input
                    :rules="rules"
                    small-chips
                    label="Select Image For Site (.jpg, .jpeg, .png, .bmp)"
                    accept=".jpg, .jpeg, .png, .bmp"
                    prepend-icon="mdi-camera"
                    v-model="siteImageFile"
                    @update:modelValue="showFilePreview"
                    @click:clear="resetImage"
                    class="pr-4"
                    variant="outlined"
                    density="compact"
                    color="primary"
                  />
                  <p class="text-caption font-weight-light">
                    *Images are cropped at 345x200px. For best results use a 3:4
                    aspect ratio. Image size limit is 50 kB.
                  </p>
                </v-col>
              </v-row>
              <v-img
                v-if="site.siteImageDataUrl"
                :src="site?.siteImageDataUrl"
                max-height="200"
                max-width="345"
              />
              <p
                class="text-caption font-weight-light"
                v-if="imageSize"
                v-bind:style="imageSize > 50 ? 'color: red' : 'color: black'"
              >
                Current image size: {{ imageSize }} kb
              </p>
              <v-btn
                title="download image"
                color="primary"
                class="mr-5 mt-4"
                @click="downloadImage"
                download
              >
                Download Image
              </v-btn>
            </div>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card>

      <div class="bottom-bar px-10">
        <v-row align="center" class="ma-0" style="height: 100%">
          <v-col cols="auto">
            <v-btn
              type="submit"
              color="primary"
              class="mr-3"
              :loading="loading"
            >
              Save
            </v-btn>
            <v-btn :to="`/sites`">Cancel</v-btn>
          </v-col>
        </v-row>
      </div>
    </v-form>
  </section>
</template>

<style>
.search-location {
  display: block;
  width: 80vw;
  outline: none;
  text-align: left;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.42);
  border-width: 0 0 thin 0;
}

.bottom-bar {
  position: fixed;
  bottom: 0;
  left: var(--v-layout-left, 0);
  right: 0;
  z-index: 10;
  max-height: 70px;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>
<script>
import Label from '@/modules/controls/_components/Label.vue';
import { Loader } from 'google-maps';
import { mask } from 'vue-the-mask';
import { mapActions } from 'vuex';
import api from '../_api';
import { createSiteStartStopTimeResourceModel } from '../_resourceModels/createSiteStartStopTimeResourceModel';
import { createStartStopTimeResourceModel } from '../_resourceModels/createStartStopTimeResourceModel';
import mapPlaceComponents from '../_resourceModels/mapAddress';
import { updateSiteContactMethodResourceModel } from '../_resourceModels/updateSiteContactMethodResourceModel';
import { updateSiteContactResourceModel } from '../_resourceModels/updateSiteContactResourceModel';
import { updateSiteResourceModel } from '../_resourceModels/updateSiteResourceModel';
import { globalConfig } from '/src/configuration/config.js';

export default {
  name: 'EditSite',
  components: {
    'label-component': Label,
  },

  directives: {
    mask,
  },

  data() {
    return {
      rules: [
        (value) =>
          !value || value.size < 3000000 || 'Image size must be less than 3MB.',
      ],
      activeTab: 'site',
      loading: true,
      valid: true,
      server_errors: [],
      emailMethod: false,
      states: [],
      weatherStations: [],
      timezones: [],
      utilityCompanies: [],
      marketContexts: [],
      siteTypes: [],
      siteStartStopTimesNew: [],
      contactTypes: [
        { value: 3, text: 'Text' },
        { value: 2, text: 'Email' },
      ],
      years: this.getYears(),
      google: null,
      autocompleted: null,
      site: new updateSiteResourceModel(),
      siteName: null,
      presetStartStopTimes: [],
      dayOfWeek: [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ],
      siteContactNotificationFrequencies: null,
      siteImageFile: null,
      imageSize: null,
    };
  },

  mounted() {
    if (this.google === null) {
      const loader = new Loader(globalConfig.get().DYNO_GOOGLE_MAPS_API_KEY, {
        libraries: ['places'],
      });
      loader
        .load()
        .then((gData) => {
          const inputField = this.$refs.autocomplete.$el.querySelector('input');
          inputField.placeholder = '';
          this.google = { ...gData };
          this.autocompleted = new this.google.maps.places.Autocomplete(
            inputField,
            { types: ['geocode'] }
          );

          this.autocompleted.addListener('place_changed', () => {
            const place = this.autocompleted.getPlace();
            const addressDetail = mapPlaceComponents(place);

            this.site.latitude = addressDetail.lat;
            this.site.longitude = addressDetail.lng;
            this.site.address = addressDetail.address;
            this.site.city = addressDetail.city;
            this.site.country = addressDetail.country;
            this.site.state = addressDetail.state;
            this.site.postalCode = addressDetail.postalCode;
          });
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }
  },

  async created() {
    this.getWeatherstations();
    this.getTimezones();
    this.getUtilityCompanies();
    this.getSiteTypes();
    this.getMarketContexts();
    this.getStates();
    this.getSiteContactFrequencies();
    this.getSite();
    this.getSites();
    this.loading = false;
  },

  computed: {
    siteCoordinatesLat: {
      get() {
        return this.site.latitude;
      },
      set(value) {
        this.site.latitude = value;
      },
    },
    siteCoordinatesLng: {
      get() {
        return this.site.longitude;
      },
      set(value) {
        this.site.longitude = value;
      },
    },
    allOpen() {
      return (
        this.siteStartStopTimesNew.length > 0 &&
        this.siteStartStopTimesNew.every((day) => day.isOpen)
      );
    },
  },

  methods: {
    toggleAllOpeningHours(state) {
      this.siteStartStopTimesNew.forEach((day) => {
        day.isOpen = state;
        if (!state) {
          day.is24h = false;
        }
      });
    },
    getFirstTimeOption() {
      const options = this.generateTimeOptions24h(false);
      return options.length > 0 ? options[0].value : null;
    },

    getLastTimeOption() {
      const options = this.generateTimeOptions24h(true);
      return options.length > 0 ? options[options.length - 1].value : null;
    },

    generateTimeOptions24h(addLastMinute) {
      const timeOptions = [];
      const periods = ['AM', 'PM'];
      const minutesInterval = 5;

      for (let periodIndex in periods) {
        for (let hour = 0; hour < 12; hour++) {
          let displayHour = hour === 0 ? 12 : hour;
          for (let minutes = 0; minutes < 60; minutes += minutesInterval) {
            // Convert to 24-hour format
            let hour24 =
              periodIndex == 1
                ? hour === 0
                  ? 12
                  : hour + 12
                : hour === 0
                  ? 0
                  : hour;
            let formatted24Hours = hour24.toString().padStart(2, '0');
            let formattedMinutes = minutes.toString().padStart(2, '0');
            let value = `${formatted24Hours}:${formattedMinutes}:00`;

            timeOptions.push({
              text: `${displayHour}:${formattedMinutes} ${periods[periodIndex]}`,
              value,
            });
          }
        }
      }

      if (addLastMinute) {
        timeOptions.push({ text: '11:59 PM (last minute)', value: '23:59:00' });
      }

      return timeOptions;
    },

    ...mapActions({
      setDefaultSite: 'session/setDefaultSite',
    }),

    getStates() {
      this.states = api.getStates();
    },

    async getSites() {
      try {
        let response = await api.getSites();
        response.forEach((site) => {
          site.longitude = site.longitude.toFixed(5);
        });
        this.sites = response;
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
    },

    async getWeatherstations() {
      try {
        let response = await api.getWeatherStations();
        this.weatherStations = response;
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
    },

    async getTimezones() {
      try {
        let response = await api.getTimezones();
        this.timezones = response;
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
    },

    async getUtilityCompanies() {
      try {
        let response = await api.getUtilityCompanies();
        this.utilityCompanies = response;
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
    },

    async getMarketContexts() {
      try {
        let response = await api.getMarketContexts();
        this.marketContexts = response;
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
    },

    async getSiteTypes() {
      try {
        let response = await api.getSiteTypes();
        this.siteTypes = response;
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
    },

    async getSiteContactFrequencies() {
      try {
        let response = await api.getSiteContactNotificationFrequencies();
        this.siteContactNotificationFrequencies = response;
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
    },

    async getSite() {
      try {
        let response = await api.getSiteById(this.$route.params.id);
        this.site = response;
        this.siteName = response.name;

        this.siteStartStopTimesNew = this.mapSiteStartStopTimeNew(
          this.site.siteStartStopTime
        );

        this.imageSize = this.site.imageSize;
        this.setDefaultSite(this.site);
      } catch (error) {
        this.server_errors = this.errorSummary(error);
      }
      this.loading = false;
    },

    mapSiteStartStopTimeNew(sssTimes) {
      let mappedTimes = [];

      for (let i = 0; i < this.dayOfWeek.length; i++) {
        let existingSssTime = sssTimes.find((t) => t.dayOfWeekId === i);
        if (existingSssTime) {
          existingSssTime.isOpen = true;
          existingSssTime.is24h =
            existingSssTime.startStopTime.siteStartTime === '00:00:00' &&
            existingSssTime.startStopTime.siteEndTime === '23:59:00';
          mappedTimes.push(existingSssTime);
        } else {
          let newSssTime = this.newStartStopTime(i, this.dayOfWeek[i], false);
          mappedTimes.push(newSssTime);
        }
      }

      return mappedTimes;
    },

    newStartStopTime(
      index,
      dayOfWeekName,
      isOpen,
      startStopTime = new createStartStopTimeResourceModel(
        '07:00:00',
        '19:00:00'
      )
    ) {
      let newSssTime = new createSiteStartStopTimeResourceModel(
        this.$route.params.id,
        index,
        dayOfWeekName,
        isOpen
      );
      startStopTime.id = 0; //TODO: make an update resource model
      newSssTime.startStopTime = startStopTime;
      newSssTime.id = 0;
      newSssTime.is24 = false;
      return newSssTime;
    },

    async onSubmit(event) {
      const { valid } = await event;
      if (!valid) {
        return Promise.reject({
          Error: 'Please fill in all required fields',
        });
      }
      this.site.thresholdkW = this.toNullableNumber(this.site.thresholdkW);
      try {
        this.loading = true;
        this.site.siteStartStopTime = this.mapUpdatedStartStopTime(
          this.siteStartStopTimesNew
        );
        await api.updateSite(this.site).then(() => {
          if (this.siteImageFile) {
            api
              .updateSiteImage(this.site.siteId, this.siteImageFile)
              .then(() => {
                this.loading = false;
                const options = {
                  title: 'Success',
                  message: 'Site updated with image.',
                  status: 'success',
                };
                this.$toast.show(options);
                this.$router.push({ name: 'SitesRoute' });
              });
          } else {
            this.loading = false;
            const options = {
              title: 'Success',
              message: 'Site updated.',
              status: 'success',
            };
            this.$toast.show(options);
            this.$router.push({ name: 'SitesRoute' });
          }
        });
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.loading = false;
      }
    },

    mapUpdatedStartStopTime(sSSTimes) {
      const openSSSTimes = sSSTimes.filter(
        (t) => t.isOpen === true && t.dayOfWeekName !== 'All'
      );
      return openSSSTimes;
    },

    toNullableNumber(value) {
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    },

    getYears() {
      let thisYear = new Date().getFullYear(),
        yearList = [];
      let startYear = 1900;
      while (startYear <= thisYear) {
        yearList.push(thisYear--);
      }
      return yearList;
    },

    addContact() {
      let newContact = new updateSiteContactResourceModel();
      this.site.updateSiteContactResourceModels.unshift(newContact);
    },

    removeContact(index) {
      this.site.updateSiteContactResourceModels.splice(index, 1);
    },

    addContactMethod(contact) {
      let newContactMethod = new updateSiteContactMethodResourceModel(
        null,
        null,
        false
      );
      contact.updateSiteContactMethodResourceModels.unshift(newContactMethod);
    },

    removeContactMethod(contact, index) {
      contact.updateSiteContactMethodResourceModels.splice(index, 1);
    },

    togglePrefer(index, contactMethod, contact) {
      let value = contactMethod.isPreferred;

      if (
        value === false ||
        contact.updateSiteContactMethodResourceModels.length === 1
      ) {
        return;
      } else {
        for (
          let i = 0;
          i < contact.updateSiteContactMethodResourceModels.length;
          i++
        ) {
          if (i === index) {
            continue;
          } else {
            contact.updateSiteContactMethodResourceModels[i].isPreferred =
              false;
          }
        }
      }
    },

    showFilePreview(e) {
      if (e) {
        this.imageSize = e.size / 1024;
        this.site.siteImageDataUrl = URL.createObjectURL(e);
      }
    },

    resetImage() {
      this.site.siteImageDataUrl = null;
      this.imageSize = null;
    },

    errorSummary(error) {
      return error.response
        ? error.response.data.Errors
          ? error.response.data.Errors
          : error.response.data.errors
            ? error.response.data.errors
            : [error]
        : [error];
    },

    timezoneFilter(item, queryText) {
      const textOne = item.name.toLowerCase();
      const searchText = queryText.toLowerCase();

      return textOne.indexOf(searchText) > -1;
    },

    downloadImage() {
      try {
        let fileType = this.site.siteImageDataUrl.split(';')[0].split('/')[1];
        let linkSource = this.site.siteImageDataUrl;
        let fileLink = document.createElement('a');
        fileLink.href = linkSource;
        fileLink.setAttribute(
          'download',
          this.site.name + ' - Image.' + fileType
        );
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        return Promise.reject(error);
      }
      this.loading = false;
    },

    handleIs24hChange(day) {
      if (day.is24h) {
        const startTimes = this.generateTimeOptions24h(false);
        const endTimes = this.generateTimeOptions24h(true);

        if (startTimes.length > 0) {
          day.startStopTime.siteStartTime = startTimes[0].value;
        }
        if (endTimes.length > 0) {
          day.startStopTime.siteEndTime = endTimes[endTimes.length - 1].value;
        }
      }
    },
  },
};
</script>
